"use client";

import Image from "next/image";
import SectionTitle from "@/app/components/atomic/molecules/SectionTitle";
import AppButton from "@/app/components/atomic/molecules/AppButton";
import {ReactNode, useRef, useState} from "react";
import GoodToKnow from "@/public/images/homepage/Img/GoodToKnow.png";
import BlurShape from "@/app/components/atomic/atoms/BlurShape";
import ChevronIcon from "@/app/components/homepage/components/ChevronIcon";
import AppIcon from "@/app/components/atomic/AppIcon";
import InlineLink from "@/app/components/homepage/components/InlineLink";

function CollapsibleBlock({id, children}: { id: string; children: ReactNode }) {
    const [isExpanded, setIsExpanded] = useState(false);
    const contentRef = useRef<HTMLDivElement>(null);
    const panelId = `collapsible-panel-${id}`;
    const buttonId = `collapsible-btn-${id}`;

    return (
        <div className="flex flex-col gap-4">
            <div
                id={panelId}
                role="region"
                aria-labelledby={buttonId}
                className="overflow-hidden transition-[max-height] duration-500 ease-in-out"
                style={{maxHeight: isExpanded ? `${contentRef.current?.scrollHeight ?? 2000}px` : "150px"}}
            >
                <div ref={contentRef}>
                    {children}
                </div>
            </div>

            <button
                id={buttonId}
                onClick={() => setIsExpanded(!isExpanded)}
                aria-expanded={isExpanded}
                aria-controls={panelId}
                className="flex items-center gap-2 text-deep-green cursor-pointer text-sm"
            >
                {isExpanded ? "Réduire" : "Développer"}
                <ChevronIcon open={isExpanded} className="text-pale" />
            </button>
        </div>
    );
}

export default function AboutSection() {
    return (
        <section className="relative overflow-x-clip py-20 px-2 md:px-6 md:pt-0 flex flex-col gap-10 max-w-[1536px] mx-auto">
            <BlurShape top="130px" width="500px" height="400px" translateX="400px" className="-z-[11] hidden md:block" />

            <BlurShape top="-70px" width="500px" height="400px" translateX="150px" className="-z-[11] md:hidden" />

            <div className="hidden md:block absolute overflow-hidden lg:rounded-r-3xl 2xl:rounded-l-3xl h-[430px] max-w-[1130px] w-full pr-6 -z-1">
                <Image
                    src={GoodToKnow}
                    alt="Le comparateur d'assurance le mieux noté"
                    className="object-cover object-[center_15%] overflow-hidden"
                    fill
                />
            </div>

            <div className="w-full md:pt-[275px] md:px-6 flex flex-col gap-10 max-w-[700px] lg:max-w-[960px] mx-auto">

                <div className="relative flex flex-col w-full items-end rounded-2xl lg:mb-10">
                    <div className="rounded-md bg-pastel-yellow p-2 flex w-fit mr-7">
                        <AppIcon iconClassName="fa-asterisk" size="md" />
                    </div>

                    <SectionTitle
                        level="h3"
                        theme="white"
                        className="mr-10"
                    >
                        Bon à savoir
                    </SectionTitle>

                    <p className="bg-pastel-blue text-pale w-[211px] rounded-md px-3 py-4 m-0">
                        Vous bénéficiez toujours de garanties équivalentes ou supérieures à celles exigées par la banque.
                    </p>
                </div>

                <CollapsibleBlock id="about-experts">
                    <div className="flex flex-col gap-4">
                        <SectionTitle
                            level="h3"
                            theme="rm-blue"
                        >
                            Sans frais cachés, <br /> des experts 100% à vos côtés
                        </SectionTitle>

                        <p>
                            Réassurez-moi, c'est avant tout une histoire de famille. L'insurtech a été fondée en 2013 par un médecin et son fils, qui s'étaient lancé le défi de rendre l'assurance accessible, transparente et surtout adaptée aux besoins réels de chacun. Depuis plus de 10 ans, on met notre expertise au service des clients, pour leur permettre de comparer facilement toutes les options sur le marché et trouver la meilleure solution. On vous accompagne à chaque étape, avec des conseils honnêtes et impartiaux. Chez Réassurez-moi, nos experts ne sont pas rémunérés à la commission, leur seul objectif demeure votre satisfaction. Notre accompagnement est sans frais de dossier, et nos éventuels frais de courtage vous sont toujours annoncés en toute transparence, avant toute souscription. C'est notamment le cas pour l'assurance de prêt immobilier, où notre rôle de courtier vous permet de <InlineLink href={"/guide/assurance-pret-immobilier/taux"}> comparer les taux d'assurance de prêt</InlineLink> et de <InlineLink href={"/guide/assurance-pret-immobilier/calcul"}>calculer le coût de votre assurance emprunteur</InlineLink>. Vous êtes ainsi certain de faire le bon choix, en toute transparence et sans frais cachés. Réassurez-moi met également la transparence au cœur de son accompagnement. Nos experts travaillent uniquement pour vous offrir le produit le plus avantageux, au meilleur tarif. Ils restent à vos côtés pour vous guider de A à Z.
                        </p>
                    </div>
                </CollapsibleBlock>

                <div className="w-full h-[1px] bg-rm-gray/20" />

                <CollapsibleBlock id="about-comparateur">
                    <div className="flex flex-col gap-4">
                        <SectionTitle
                            level="h3"
                            theme="rm-blue"
                        >
                            Comment fonctionne notre <br />comparateur d'assurances ?
                        </SectionTitle>

                        <p>
                            En quelques clics, vous renseignez votre profil et vos besoins pour filtrer les résultats. L'outil vous propose alors une sélection d'offres personnalisées, clairement classées avec tarifs et garanties, accompagnées de l'avis d'experts pour vous aider à faire un choix éclairé, en toute transparence. Avec notre <InlineLink href="/comparateur-mutuelle-sante">comparateur de mutuelle santé</InlineLink> par exemple, vous visualisez d'un coup d'œil les garanties et les tarifs adaptés à votre situation.
                        </p>
                    </div>
                </CollapsibleBlock>

                <div className="w-full h-[1px] bg-rm-gray/20" />

                <CollapsibleBlock id="about-devis">
                    <div className="flex flex-col gap-4">
                        <SectionTitle
                            level="h3"
                            theme="rm-blue"
                        >
                            Obtenez un devis personnalisé <br />et souscrivez en ligne gratuitement
                        </SectionTitle>

                        <p>
                            Après la comparaison, vous utilisez des simulateurs rapides et gratuits pour obtenir en quelques minutes plusieurs devis personnalisés. Vous pouvez par exemple <InlineLink href={"/guide/pret-immobilier/simulation"}>simuler votre assurance de prêt immobilier</InlineLink>, réaliser une <InlineLink href={"/guide/pret-immobilier"}>simulation de prêt immobilier</InlineLink> ou vérifier les <InlineLink href={"/taux-pret-immobilier"}>taux de prêt immobilier</InlineLink> du moment. Pour votre assurance emprunteur, vous comparez facilement tarifs et garanties afin de choisir le meilleur équilibre prix/protection, puis un expert dédié vous accompagne de A à Z, de la souscription au changement de contrat, et reste à vos côtés même après signature.
                        </p>
                    </div>
                </CollapsibleBlock>

                <div className="mx-auto">
                    <AppButton arrowPosition="before" href="#comparators" smoothScroll>
                        Je compare gratuitement
                    </AppButton>
                </div>
            </div>
        </section>
    );
}
